import { privateApi } from '@/config/axios.config';

const API_BASE = '/condominium/residents';

export interface Resident {
  id: string;
  name: string;
  email: string;
  phone: string;
  documentId?: string;
  apartment: string;
  tower: string;
  unit?: string; // Alias para apartment
  status: 'Activo' | 'Moroso' | 'Inactivo';
  paymentStatus: 'Al día' | 'Pendiente' | 'Vencido';
  occupancyStatus?: 'owner' | 'renter';
  vehicles?: number;
  moveInDate: string;
  moveOutDate?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ResidentCreateDTO {
  name: string;
  email: string;
  phone: string;
  documentId?: string;
  apartment: string;
  tower: string;
  moveInDate: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface ResidentUpdateDTO extends Partial<ResidentCreateDTO> {
  id: string;
  status?: string;
  paymentStatus?: string;
  moveOutDate?: string;
  occupancyStatus?: 'owner' | 'renter';
  vehicles?: number;
}

export interface ResidentFilters {
  status?: string;
  paymentStatus?: string;
  tower?: string;
  apartment?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ResidentStats {
  totalResidents: number;
  activeResidents: number;
  morosoResidents: number;
  inactiveResidents: number;
  residentsByTower: Record<string, number>;
  residentsByPaymentStatus: Record<string, number>;
}

export const residentsApiService = {
  // Obtener todos los residentes con filtros
  async getResidents(filters: ResidentFilters = {}): Promise<{
    residents: Resident[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.paymentStatus)
      params.append('paymentStatus', filters.paymentStatus);
    if (filters.tower) params.append('tower', filters.tower);
    if (filters.apartment) params.append('apartment', filters.apartment);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    return response.data?.data || response.data;
  },

  // Obtener un residente por ID
  async getResidentById(id: string): Promise<Resident> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Crear nuevo residente
  async createResident(residentData: ResidentCreateDTO): Promise<Resident> {
    const response = await privateApi.post(`${API_BASE}/`, residentData);
    return response.data?.data || response.data;
  },

  // Actualizar residente
  async updateResident(
    id: string,
    residentData: ResidentUpdateDTO
  ): Promise<Resident> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, residentData);
    return response.data?.data || response.data;
  },

  // Eliminar residente
  async deleteResident(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Cambiar estado de un residente (toggle)
  async toggleResidentStatus(id: string): Promise<Resident> {
    const response = await privateApi.patch(`${API_BASE}/${id}/toggle-status`);
    return response.data?.data || response.data;
  },

  // Obtener estadísticas de residentes
  async getResidentStats(): Promise<ResidentStats> {
    const response = await privateApi.get(`${API_BASE}/stats`);
    return response.data?.data || response.data;
  },

  // Actualizar estado del residente
  async updateResidentStatus(id: string, status: string): Promise<Resident> {
    const response = await privateApi.patch(`${API_BASE}/${id}/status`, { status });
    return response.data?.data || response.data;
  },

  // Actualizar estado de pago del residente
  async updatePaymentStatus(
    id: string,
    paymentStatus: string
  ): Promise<Resident> {
    const response = await privateApi.patch(`${API_BASE}/${id}/payment-status`, {
      paymentStatus,
    });
    return response.data?.data || response.data;
  },

  // Registrar mudanza de entrada
  async registerMoveIn(
    id: string,
    moveInData: {
      moveInDate: string;
      apartment: string;
      tower: string;
    }
  ): Promise<Resident> {
    const response = await privateApi.post(`${API_BASE}/${id}/move-in`, moveInData);
    return response.data?.data || response.data;
  },

  // Registrar mudanza de salida
  async registerMoveOut(
    id: string,
    moveOutData: {
      moveOutDate: string;
      reason?: string;
    }
  ): Promise<Resident> {
    const response = await privateApi.post(`${API_BASE}/${id}/move-out`, moveOutData);
    return response.data?.data || response.data;
  },

  // Obtener historial de pagos del residente
  async getPaymentHistory(id: string): Promise<{
    payments: Array<{
      id: string;
      amount: number;
      dueDate: string;
      paidDate?: string;
      status: string;
      description: string;
    }>;
  }> {
    const response = await privateApi.get(`${API_BASE}/${id}/payments`);
    return response.data?.data || response.data;
  },

  // Obtener residentes eliminados (papelera)
  async getDeletedResidents(filters: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    residents: Resident[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}/trash?${params.toString()}`);
    const data = response.data?.data || response.data;
    return {
      residents: Array.isArray(data?.residents) ? data.residents : (Array.isArray(data) ? data : []),
      total: data?.total || 0,
      page: data?.page || 1,
      limit: data?.limit || 10,
      totalPages: data?.totalPages || 0,
    };
  },

  // Restaurar residente eliminado
  async restoreResident(id: string): Promise<Resident> {
    const response = await privateApi.post(`${API_BASE}/${id}/restore`);
    return response.data?.data || response.data;
  },

  // Eliminar permanentemente
  async permanentDeleteResident(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}/permanent`);
  },

  // Descargar template para carga masiva
  async downloadBulkUploadTemplate(): Promise<Blob> {
    const response = await privateApi.get(`${API_BASE}/bulk/template`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Subir archivo para carga masiva (retorna jobId para consultar estado)
  async uploadBulkResidents(file: File): Promise<{
    jobId: string;
    message: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    // NO establecer Content-Type manualmente - axios lo hace automáticamente con boundary
    const response = await privateApi.post(`${API_BASE}/bulk/upload`, formData, {
      headers: {
        // Dejar que axios establezca automáticamente Content-Type: multipart/form-data; boundary=...
      },
    });
    return response.data?.data || response.data;
  },

  // Consultar estado del job de carga masiva
  async getBulkUploadStatus(jobId: string): Promise<{
    jobId: string;
    status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
    progress: number;
    result?: {
      success: number;
      failed: number;
      errors?: Array<{
        row: number;
        errors: string[];
      }>;
    };
    error?: string;
  }> {
    const response = await privateApi.get(`${API_BASE}/bulk/upload/${jobId}/status`);
    return response.data?.data || response.data;
  },
};

export default residentsApiService;
